import { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { fmt } from "../../services/formatting";

const getChartPixelRatio = () => {
  const ratio = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
  return Math.max(2, ratio * 1.5);
};

export default function SimplifiedBankrollChart({
  chartColors,
  chartData,
  chartFilters,
  clearChartFilters,
  hasActiveFilters,
  hasSourceHistory,
  platformOptions,
  pointLabels,
  setChartFilter,
  sortedHistory,
  theme,
  displayCurrency,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef(null);
  const activeFilterCount = [
    chartFilters.startDate,
    chartFilters.endDate,
    ...(chartFilters.platforms || []),
  ].filter(Boolean).length;
  const selectedPlatforms = chartFilters.platforms || [];

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const closeFiltersOnOutsideClick = (event) => {
      if (!filtersRef.current?.contains(event.target)) {
        setFiltersOpen(false);
      }
    };

    const closeFiltersOnEscape = (event) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
      }
    };

    document.addEventListener("mousedown", closeFiltersOnOutsideClick);
    document.addEventListener("touchstart", closeFiltersOnOutsideClick);
    document.addEventListener("keydown", closeFiltersOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeFiltersOnOutsideClick);
      document.removeEventListener("touchstart", closeFiltersOnOutsideClick);
      document.removeEventListener("keydown", closeFiltersOnEscape);
    };
  }, [filtersOpen]);

  const toggleChartPlatform = (platform) => {
    const nextPlatforms = selectedPlatforms.includes(platform)
      ? selectedPlatforms.filter((currentPlatform) => currentPlatform !== platform)
      : [...selectedPlatforms, platform];

    setChartFilter("platforms", nextPlatforms);
  };

  if (!hasSourceHistory) return null;

  return (
    <div className="simple-chart-card">
      <div className="simple-chart-card__header">
        <div>
          <h2>Lucro Historico</h2>
          <span>labels mostram a variacao vs. dia anterior</span>
        </div>
        <div className="simple-chart-filter-shell" ref={filtersRef}>
          <button
            className={`filter-toggle-button simple-chart-filter-toggle ${
              hasActiveFilters ? "filter-toggle-button--active" : ""
            }`}
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls="simple-chart-filters"
          >
            Filtros
            {activeFilterCount > 0 && (
              <span className="filter-badge">{activeFilterCount}</span>
            )}
          </button>
          {filtersOpen && (
            <div
              id="simple-chart-filters"
              className="simple-chart-filters motion-expand-panel"
              aria-label="Filtros do grafico"
            >
              <div className="simple-chart-filter-row">
                <label className="simple-chart-filter">
                  <span>De</span>
                  <input
                    className="input-field"
                    type="date"
                    value={chartFilters.startDate}
                    onChange={(event) =>
                      setChartFilter("startDate", event.target.value)
                    }
                  />
                </label>
                <label className="simple-chart-filter">
                  <span>Ate</span>
                  <input
                    className="input-field"
                    type="date"
                    value={chartFilters.endDate}
                    onChange={(event) =>
                      setChartFilter("endDate", event.target.value)
                    }
                  />
                </label>
              </div>

              <fieldset className="simple-chart-filter simple-chart-filter--platform">
                <legend>Plataforma</legend>
                <div className="simple-chart-platform-list">
                  {platformOptions.map((platform) => (
                    <label key={platform} className="simple-chart-platform-option">
                      <input
                        type="checkbox"
                        checked={selectedPlatforms.includes(platform)}
                        onChange={() => toggleChartPlatform(platform)}
                      />
                      <span>{platform}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="simple-chart-filter-actions">
                {hasActiveFilters && (
                  <button
                    className="btn-ghost simple-chart-filter__clear"
                    type="button"
                    onClick={clearChartFilters}
                  >
                    Limpar
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {sortedHistory.length ? (
        <Line
          data={chartData}
          options={{
            devicePixelRatio: getChartPixelRatio(),
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: {
                display: true,
                labels: {
                  font: { family: "'Crimson Text', serif", size: 12 },
                  color: chartColors.text,
                },
              },
              tooltip: {
                backgroundColor:
                  theme === "light"
                    ? "rgba(248,250,249,0.96)"
                    : "rgba(0,0,0,0.8)",
                titleColor: theme === "light" ? "#26221d" : "#e8dcc8",
                bodyColor: chartColors.text,
                borderColor: chartColors.accent,
                borderWidth: 1,
                padding: 12,
                displayColors: false,
                callbacks: {
                  label: (context) =>
                    `Resultado: ${fmt(context.parsed.y, displayCurrency)} - Dia: ${
                      pointLabels[context.dataIndex]
                    }`,
                },
                titleFont: { family: "'Spectral', serif", size: 11 },
                bodyFont: { family: "'Crimson Text', serif", size: 12 },
              },
            },
            scales: {
              y: {
                ticks: {
                  font: { family: "'Crimson Text',serif", size: 11 },
                  color: chartColors.soft,
                  callback: (value) =>
                    fmt(value, displayCurrency, { minimumFractionDigits: 0 }),
                },
                grid: {
                  color: chartColors.grid,
                  drawBorder: false,
                },
              },
              x: {
                ticks: {
                  font: { family: "'Crimson Text',serif", size: 11 },
                  color: chartColors.soft,
                },
                grid: {
                  display: false,
                  drawBorder: false,
                },
              },
            },
          }}
        />
      ) : (
        <div className="simple-chart-empty">
          Nenhum registro encontrado para esse filtro.
        </div>
      )}
    </div>
  );
}
